export interface NotifActor {
  id: number;
  username: string;
  avatar_url: string | null;
}

export interface NotifDuel {
  id: number;
  name: string;
}

export interface NotifTarget {
  kind: 'track' | 'duel_result' | 'duel_play' | 'profile' | 'challenge' | 'season' | 'none';
  id?: number;
  token?: string;
  username?: string;
}

export interface NotifItem {
  id: number;
  type: string;
  actor_id: number | null;
  duel_id: number | null;
  track_id: number | null;
  data: string | null;
  is_read: boolean;
  created_at: string;
  actor?: NotifActor;
  duel?: NotifDuel;
  /** Grouped notification fields (vote/top_vote consolidation) */
  grouped?: boolean;
  actors?: NotifActor[];
  actor_count?: number;
  grouped_ids?: number[];
  message?: string;
  /** Server-canonical deep-link descriptor (web + iOS consume identically). */
  target?: NotifTarget;
}

export function tryParseNotifData(s: string | null): Record<string, unknown> | null {
  if (!s) return null;
  try {
    return JSON.parse(s);
  } catch {
    return null;
  }
}

export function notifMessage(n: NotifItem): string {
  // Grouped notifications have a pre-formatted message from the backend
  if (n.grouped && n.message) {
    return n.message;
  }

  const actor = n.actor?.username ?? 'Someone';
  const duel = n.duel?.name ?? 'a duel';
  const noteData = tryParseNotifData(n.data);

  switch (n.type) {
    case 'new_follower':
      return `${actor} followed you`;
    case 'vote':
      return `${actor} voted on your track`;
    case 'top_vote':
      return `${actor} top-voted your track`;
    case 'vote_milestone': {
      const count = noteData?.milestone ?? '';
      return `Your track hit ${count} votes`;
    }
    case 'duel_end':
      return `${duel} has ended`;
    case 'duel_ending_soon':
      return `${duel} is ending soon`;
    case 'bot_kudos':
      return `${actor}: ${noteData?.message ?? 'Great track!'}`;
    case 'challenge_completed':
      return `${actor} took your challenge`;
    case 'challenge_received':
      return `${actor} challenged you to a match`;
    case 'season_ended': {
      const num = noteData?.season_number ?? '';
      return num ? `Week ${num} ended — see your final standings` : 'The week ended — see your final standings';
    }
    default:
      return `New notification`;
  }
}

export function notifLink(n: NotifItem): string | null {
  // Prefer the server-canonical target descriptor when present.
  if (n.target) {
    switch (n.target.kind) {
      case 'track':
        return n.target.id != null ? `/tracks/${n.target.id}` : null;
      case 'duel_result':
        return n.target.id != null ? `/duels/${n.target.id}/result` : null;
      case 'duel_play':
        return n.target.id != null ? `/duels/${n.target.id}` : null;
      case 'profile':
        return n.target.username ? `/users/${n.target.username}` : null;
      case 'challenge':
        return n.target.token ? `/challenge/${n.target.token}` : null;
      case 'season':
        return '/season-wrap-up';
      case 'none':
      default:
        return null;
    }
  }

  return null;
}

export function notifIcon(type: string): string {
  switch (type) {
    case 'new_follower':
      return '\u{1F464}';
    case 'vote':
      return '\u{1F44D}';
    case 'top_vote':
      return '\u2B50';
    case 'vote_milestone':
      return '\u{1F3B5}';
    case 'duel_end':
      return '\u{1F3C6}';
    case 'duel_ending_soon':
      return '\u23F0';
    case 'bot_kudos':
      return '\u{1F916}';
    case 'challenge_completed':
      return '\u2694\uFE0F';
    case 'challenge_received':
      return '\u{1F3AF}';
    case 'season_ended':
      return '\u{1F3C1}';
    default:
      return '\u{1F514}';
  }
}

/**
 * Compact time format for dropdown: 1m, 2h, 3d, 1w
 */
export function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'now';
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d`;
  return `${Math.floor(days / 7)}w`;
}

/**
 * Longer format for list page: 1m ago, 2h ago, or locale date string
 */
export function formatNotifDate(dateStr: string): string {
  const d = new Date(dateStr);
  const diff = Date.now() - d.getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString();
}
